// Build the srcdoc for the sandboxed artifact-render iframe (SCE-12121 Phase 1).
//
// Artifact HTML is untrusted marketing content. Rendering it inside an iframe
// whose `sandbox` omits `allow-scripts` means its <script> tags and inline
// event handlers never execute -- this neutralizes the XSS sink that the old
// dangerouslySetInnerHTML rendering exposed.
//
// Note on CSP: a srcdoc iframe INHERITS the parent document's CSP and can only
// intersect (further restrict) it via its own <meta> -- it cannot relax it. So
// the marketing HTML's external fonts/images load only because the portal's
// img-src/font-src permit https: (see index.html); injecting a CSP meta here
// would be useless (and can only make things stricter), so we don't.

// Open links in a new tab (the iframe's own top-navigation is sandboxed away).
const HEAD_INJECT = '<base target="_blank">';

const isCompleteHtmlDocument = (html: string): boolean => {
  const trimmed = html.trim().toLowerCase();
  return trimmed.startsWith('<!doctype html') || trimmed.startsWith('<html');
};

/**
 * Produce the iframe `srcdoc` string for the given artifact HTML. Complete HTML
 * documents get the CSP/base injected into their <head> (or right after <html>);
 * fragments are wrapped in a minimal document.
 */
export const buildArtifactSrcDoc = (html: string): string => {
  if (!isCompleteHtmlDocument(html)) {
    return `<!DOCTYPE html><html><head><meta charset="utf-8">${HEAD_INJECT}</head><body>${html}</body></html>`;
  }

  // Inject just after the opening <head> tag when present, else after <html>.
  const headOpen = /<head\b[^>]*>/i;
  if (headOpen.test(html)) {
    return html.replace(headOpen, (match) => `${match}${HEAD_INJECT}`);
  }
  const htmlOpen = /<html\b[^>]*>/i;
  if (htmlOpen.test(html)) {
    return html.replace(htmlOpen, (match) => `${match}<head>${HEAD_INJECT}</head>`);
  }
  // No <html>/<head> despite the doctype -- prepend a head-bearing shell.
  return `${HEAD_INJECT}${html}`;
};

// sandbox tokens for the render iframe: NO allow-scripts (scripts stay inert) and
// NO allow-same-origin (opaque origin, cannot reach the portal document). Popups
// are allowed so external links opened by the user still work.
export const ARTIFACT_FRAME_SANDBOX = 'allow-popups allow-popups-to-escape-sandbox';
